import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useLadder } from '../contexts/LadderContext';
import { Users, ChevronDown, ChevronUp, Check, RotateCcw, ChevronRight } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { generatePlayerSlug } from './PlayerProfile';

// Player card with expandable partner info
const PlayerPartnerCard = ({ playerData, allPlayers, leaderboard, t, language }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Handle both array and object leaderboard formats
  const qualifiedPlayers = Array.isArray(leaderboard) ? leaderboard : (leaderboard?.qualified || []);
  
  // Create a map of player_id -> rank from leaderboard
  const rankMap = {};
  qualifiedPlayers.forEach((entry) => {
    rankMap[entry.player_id] = entry.rank;
  });
  
  // Use API's not_played_with if available, otherwise calculate from partners
  const notPlayedWith = playerData.not_played_with || (() => {
    const playedPartnerIds = new Set(playerData.partners.map(p => p.partner_id));
    const otherPlayers = allPlayers.filter(p => p.id !== playerData.player_id);
    return otherPlayers
      .filter(p => !playedPartnerIds.has(p.id))
      .map(p => ({ partner_id: p.id, partner_name: p.name }));
  })();
  
  // Add rank to not played with
  const notPlayedWithRanked = notPlayedWith.map(p => ({
    ...p,
    rank: rankMap[p.partner_id] || '-'
  }));
  
  const hasFullRotation = notPlayedWithRanked.length === 0 && allPlayers.length > 1;

  // Partner count text
  const partnerCount = playerData.partners?.length || 0;
  const partnerCountText = language === 'el' 
    ? `${partnerCount} συμπαίκτες`
    : `${partnerCount} partners`;

  // Helper for singular/plural matches
  const getMatchText = (count) => {
    return count === 1 ? t('gamePlayed') : t('gamesPlayed');
  };

  // Get first name for stats button
  const firstName = playerData.player_name.split(' ')[0];

  return (
    <div 
      className="bg-card rounded-xl border border-border overflow-hidden"
      data-testid={`partner-card-${playerData.player_id}`}
    >
      {/* Header - Clickable */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-muted rounded-full flex items-center justify-center">
            <span className="text-sm font-semibold text-foreground">
              {playerData.player_name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="text-left">
            <p className="font-semibold text-foreground">{playerData.player_name}</p>
            <p className="text-xs text-muted-foreground">
              {partnerCountText}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasFullRotation && (
            <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
              <Check className="h-3 w-3" />
            </span>
          )}
          {expanded ? (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-border p-4 space-y-4">
          {/* Full Rotation Achievement */}
          {hasFullRotation && (
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2">
              <Check className="h-4 w-4" />
              <span>{t('fullRotation')}</span>
            </div>
          )}

          {/* Played With */}
          {partnerCount > 0 ? (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">
                {t('playedWith')}
              </p>
              <div className="space-y-1.5">
                {playerData.partners.map(partner => (
                  <Link 
                    key={partner.partner_id}
                    to={`/p/${partner.partner_id}/${generatePlayerSlug(partner.partner_name, allPlayers)}`}
                    className="flex items-center justify-between py-1.5 px-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                  >
                    <span className="text-sm font-medium text-foreground">
                      {partner.partner_name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {partner.matches_together} {getMatchText(partner.matches_together)}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-2 bg-muted/30 rounded-lg">
              {t('noMatchesYet')}
            </div>
          )}

          {/* Not Played With */}
          {notPlayedWithRanked.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-muted-foreground mb-2">
                {t('notPlayedWith')}
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {notPlayedWithRanked.map(player => (
                  <Link 
                    key={player.partner_id}
                    to={`/p/${player.partner_id}/${generatePlayerSlug(player.partner_name, allPlayers)}`}
                    className="flex items-center justify-between py-1.5 px-3 bg-muted/30 rounded-lg opacity-60 hover:opacity-100 hover:bg-muted/50 transition-all"
                  >
                    <span className="text-sm text-foreground truncate">
                      {player.partner_name.split(' ')[0]}
                    </span>
                    <span className="text-xs text-muted-foreground font-medium">
                      {player.rank !== '-' ? `#${player.rank}` : '-'}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Stats Button */}
          <Link
            to={`/p/${playerData.player_id}/${generatePlayerSlug(playerData.player_name, allPlayers)}`}
            className="mt-4 w-full flex items-center justify-center gap-1 py-2 text-sm text-primary hover:text-primary/80 transition-colors border-t border-border"
            data-testid={`stats-link-${playerData.player_id}`}
          >
            {firstName} {t('playerStats')} <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default function Partners() {
  const { t, language } = useLanguage();
  const { partners, players, leaderboard, loading } = useLadder();
  const [isLandscape, setIsLandscape] = useState(false);

  // Detect orientation
  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    return () => window.removeEventListener('resize', checkOrientation);
  }, []);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="partners-loading">
        <Skeleton className="h-10 w-64 mb-2" />
        <Skeleton className="h-5 w-96 mb-8" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={`skeleton-${i}`} className="h-20 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Use partners data from API (includes not_played_with), fall back to players list
  const playerPartnerData = partners.length > 0 
    ? partners.sort((a, b) => a.player_name.localeCompare(b.player_name))
    : players.map(player => ({
        player_id: player.id,
        player_name: player.name,
        partners: [],
        not_played_with: players.filter(p => p.id !== player.id).map(p => ({
          partner_id: p.id,
          partner_name: p.name
        }))
      })).sort((a, b) => a.player_name.localeCompare(b.player_name));

  // Page title based on language
  const pageTitle = language === 'el' ? 'Tracking Συμπαικτών' : 'Partners Tracking';

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="partners-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground mb-1">{pageTitle}</h1>
        <p className="text-sm text-muted-foreground">{t('partnersDesc')}</p>
      </div>

      {players.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-xl border border-border" data-testid="no-partners">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t('noPartnersYet')}</p>
        </div>
      ) : (
        <>
          {/* Player Cards */}
          <div className="space-y-3" data-testid="partners-list">
            {playerPartnerData.map(playerData => (
              <PlayerPartnerCard 
                key={playerData.player_id}
                playerData={playerData}
                allPlayers={players}
                leaderboard={leaderboard}
                t={t}
                language={language}
              />
            ))}
          </div>

          {/* Mobile Matrix Hint (portrait only) */}
          {!isLandscape && players.length <= 14 && (
            <div className="lg:hidden mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg py-3">
              <RotateCcw className="h-4 w-4" />
              <span>{t('rotateForMatrix')}</span>
            </div>
          )}

          {/* Matrix view - Desktop + Mobile Landscape */}
          {players.length <= 14 && players.length > 0 && (
            <div className={`mt-8 ${isLandscape ? '' : 'hidden lg:block'}`} data-testid="partners-matrix">
              <h2 className="font-heading text-lg font-semibold text-foreground mb-4">
                Partner Matrix
              </h2>
              <div className="bg-card rounded-xl border border-border p-4 overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="p-2 text-left text-xs font-semibold text-muted-foreground"></th>
                      {players.map(p => (
                        <th key={p.id} className="p-2 text-center">
                          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                            {p.name.split(' ')[0].slice(0, 4)}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {players.map(rowPlayer => {
                      const playerPartners = partners.find(p => p.player_id === rowPlayer.id)?.partners || [];
                      const partnerMap = {};
                      playerPartners.forEach(p => { partnerMap[p.partner_id] = p.matches_together; });
                      
                      return (
                        <tr key={rowPlayer.id}>
                          <td className="p-2 text-xs font-medium text-foreground whitespace-nowrap">
                            {rowPlayer.name.split(' ')[0].slice(0, 6)}
                          </td>
                          {players.map(colPlayer => {
                            const matchCount = partnerMap[colPlayer.id] || 0;
                            const isSelf = rowPlayer.id === colPlayer.id;
                            
                            return (
                              <td key={colPlayer.id} className="p-1.5 text-center">
                                <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-semibold mx-auto ${
                                  isSelf ? 'bg-muted text-muted-foreground' :
                                  matchCount > 0 ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' : 
                                  'bg-muted/50 text-muted-foreground'
                                }`}>
                                  {isSelf ? '-' : matchCount || '0'}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
